/* global Word */

// ═══════════════════════════════════════════════════════════════════════════
// Footnote Detection Utilities — shared between run9 and run12
// ═══════════════════════════════════════════════════════════════════════════

// Comprehensive footnote symbol set including ǂ (U+01C2)
const FOOTNOTE_SYMBOLS = '*§†‡¶#\u01C2¹²³⁴⁵⁶⁷⁸⁹⁰';

/**
 * Check if text starts with "Source" keyword.
 */
export function startsWithSource(text: string): boolean {
  return text ? /^Source\b/i.test(text.trim()) : false;
}

/**
 * Check if text starts with a recognized footnote symbol.
 */
export function startsWithFootnoteSymbol(text: string): boolean {
  if (!text) return false;
  if (!text.trim()) return false;
  return FOOTNOTE_SYMBOLS.includes(text.trim()[0]);
}

// ─── Superscript helpers ───────────────────────────────────────────────────

/**
 * Check OOXML of a paragraph for a superscript/subscript first real run.
 * Returns true/false/null (null = inconclusive, fall through to next method).
 */
async function checkOoxmlSuperscript(
  para: Word.Paragraph,
  context: Word.RequestContext
): Promise<boolean | null> {
  const ooxml = para.getOoxml();
  await context.sync();

  const doc  = new DOMParser().parseFromString(ooxml.value, 'text/xml');
  const NS   = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
  const runs = Array.from(doc.getElementsByTagNameNS(NS, 'r'));

  for (const run of runs) {
    const textNodes = Array.from(run.getElementsByTagNameNS(NS, 't'));
    const text = textNodes.map(t => t.textContent ?? '').join('').trim();
    if (!text) continue;

    const rPrNodes  = Array.from(run.getElementsByTagNameNS(NS, 'rPr'));
    if (rPrNodes.length > 0) {
      const vertAligns = Array.from(rPrNodes[0].getElementsByTagNameNS(NS, 'vertAlign'));
      if (vertAligns.length > 0) {
        const val = vertAligns[0].getAttributeNS(NS, 'val');
        console.log(`  [OOXML] first real run text="${text}" vertAlign="${val}"`);
        if (val === 'superscript' || val === 'subscript') {
          console.log('  [OOXML] → superscript/subscript confirmed');
          return true;
        }
      }
    }

    // First run with real text found but NOT superscript — inconclusive
    console.log('  [OOXML] → first run not superscript/subscript, falling through');
    return null;
  }

  return null;
}

/**
 * Check text ranges of a paragraph for a superscript/subscript first range.
 */
async function checkRangesSuperscript(
  para: Word.Paragraph,
  context: Word.RequestContext
): Promise<boolean> {
  const ranges = para.getTextRanges([' ', '\t', '\n'], true);
  ranges.load('items');
  await context.sync();

  if (ranges.items.length === 0) return false;

  const cnt = Math.min(3, ranges.items.length);
  for (let i = 0; i < cnt; i++) {
    ranges.items[i].font.load('superscript');
    ranges.items[i].font.load('subscript');
  }
  await context.sync();

  for (let i = 0; i < cnt; i++) {
    const sup = ranges.items[i].font.superscript;
    const sub = ranges.items[i].font.subscript;
    console.log(`  [ranges] range[${i}] superscript=${sup}`);
    if (sup === true || sub === true) {
      console.log('  [ranges] → superscript/subscript confirmed');
      return true;
    }
  }

  return false;
}

export async function firstRunIsSuperscript(
  para: Word.Paragraph,
  context: Word.RequestContext
): Promise<boolean> {
  try {
    // ── Method 1: OOXML ───────────────────────────────────────────────────
    try {
      const ooxmlResult = await checkOoxmlSuperscript(para, context);
      if (ooxmlResult !== null) return ooxmlResult;
    } catch (ooxmlErr: any) {
      console.log(`  [OOXML] failed: ${ooxmlErr?.message}`);
    }

    // ── Method 2: getTextRanges fallback ──────────────────────────────────
    return await checkRangesSuperscript(para, context);

  } catch (e: unknown) {
    // S2486: log rather than silently swallow
    console.warn('[firstRunIsSuperscript] unexpected error:', e);
    return false;
  }
}

/**
 * Check if text matches a "Key: value" colon pattern.
 * Allows apostrophes and hyphens in abbreviations: rec'd, corr'd, norm'd
 * Examples: "Note:", "IV:", "rec'd:", "F:", "HPLC:"
 *
 * S6535: hyphen moved to end of character class so it is literal, not escaped.
 */
export function hasColonPattern(text: string): boolean {
  if (!text) return false;
  if (!text.trim()) return false;
  return /^[A-Za-z][A-Za-z0-9'\u2018\u2019-]*:\s*.+/.test(text.trim());
}

/**
 * Combined footnote detection — checks all patterns in order and returns result.
 *
 * Order:
 *   1. Starts with "Source"
 *   2. Starts with a footnote symbol (*, §, †, ¹, etc.)
 *   3. First run is superscript (OOXML then getTextRanges fallback)
 *   4. Colon pattern (Key: value abbreviation)
 */
export async function detectFootnote(
  para: Word.Paragraph,
  text: string,
  context: Word.RequestContext
): Promise<{ detected: boolean; reason: string }> {
  if (!text || text.trim().length === 0) return { detected: false, reason: 'empty' };

  const t = text.trim();

  // ── 1. Source keyword ──────────────────────────────────────────────────
  if (startsWithSource(t)) {
    return { detected: true, reason: 'starts with "Source"' };
  }

  // ── 2. Footnote symbol ─────────────────────────────────────────────────
  if (startsWithFootnoteSymbol(t)) {
    return { detected: true, reason: `footnote symbol "${t[0]}"` };
  }

  // ── 3. Superscript first run ───────────────────────────────────────────
  const superRun = await firstRunIsSuperscript(para, context);
  if (superRun) {
    return { detected: true, reason: 'superscript font run' };
  }

  // ── 4. Colon pattern ───────────────────────────────────────────────────
  // S7758: codePointAt() preferred over charCodeAt() for full Unicode coverage
  console.log(`  [detectFootnote] colon check: t="${t.substring(0, 60)}"`);
  console.log(`  [detectFootnote] codePoint[0]=${t.codePointAt(0)} codePoint[1]=${t.codePointAt(1)}`);
  console.log(`  [detectFootnote] hasColonPattern=${hasColonPattern(t)}`);

  if (hasColonPattern(t)) {
    const word = t.split(':')[0];
    return { detected: true, reason: `colon pattern "${word}:"` };
  }

  return { detected: false, reason: 'no match' };
}

/**
 * Apply a paragraph style with track changes guaranteed.
 */
export async function applyStyleWithTracking(
  para: Word.Paragraph,
  styleName: string,
  context: Word.RequestContext
): Promise<void> {
  context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
  await context.sync();
  para.style = styleName;
  await context.sync();
}